import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  programmingService, errorDetail, errorStatus,
  ProgrammingCatalogue, ProgrammingCommand, ProgrammingChange, ReadItem, WriteItem, CommandOp,
} from '../../../services/programmingService';
import { DeviceDetailCore } from '../useDeviceDetail';

export type ProgrammingMode = 'read' | 'write';
export type CatalogueFailure = 'none' | 'forbidden' | 'failed';

export interface LogEntry {
  at: Date;
  kind: 'out' | 'in' | 'info' | 'error';
  text: string;
}

/** A change the user staged in write mode, keyed by command code. */
export interface StagedChange {
  op: CommandOp;
  value?: unknown;
  index?: number;
}

const MAX_LOG = 300;

/**
 * The Programming panel: the parameters living in the gateway, read and
 * written through the backend over the device's conf channel. Loads the
 * catalogue and pings the gateway the first time the panel is shown.
 */
export const useDevicesDetailsProgramming = (core: DeviceDetailCore, active: boolean) => {
  const { device, isOpen } = core;
  const deviceId = device?.idDevice ?? '';

  const [catalogue, setCatalogue] = useState<ProgrammingCatalogue | null>(null);
  const [catalogueFailure, setCatalogueFailure] = useState<CatalogueFailure | null>(null);
  const [online, setOnline] = useState<boolean | null>(null);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [identification, setIdentification] = useState<string | null>(null);
  const [pinging, setPinging] = useState(false);
  const [mode, setMode] = useState<ProgrammingMode>('read');
  const [values, setValues] = useState<Record<string, ReadItem>>({});
  const [readingGroup, setReadingGroup] = useState<string | null>(null);
  const [readProgress, setReadProgress] = useState<{ done: number; total: number } | null>(null);
  const [staged, setStaged] = useState<Record<string, StagedChange>>({});
  const [indexes, setIndexes] = useState<Record<string, number>>({});
  const [writing, setWriting] = useState(false);
  const [lastResults, setLastResults] = useState<WriteItem[]>([]);
  const [lastReadAt, setLastReadAt] = useState<Date | null>(null);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [rawBusy, setRawBusy] = useState(false);
  const loadedFor = useRef<string | null>(null);

  const addLog = useCallback((kind: LogEntry['kind'], text: string) => {
    setLog((prev) => [...prev.slice(-(MAX_LOG - 1)), { at: new Date(), kind, text }]);
  }, []);
  const clearLog = useCallback(() => setLog([]), []);

  // A new device or a re-open starts clean.
  useEffect(() => {
    if (!isOpen) return;
    loadedFor.current = null;
    setCatalogue(null);
    setCatalogueFailure(null);
    setOnline(null);
    setLatencyMs(null);
    setIdentification(null);
    setMode('read');
    setValues({});
    setStaged({});
    setIndexes({});
    setLastResults([]);
    setLastReadAt(null);
    setLog([]);
    setReadProgress(null);
  }, [isOpen, deviceId]);

  const ping = useCallback(async () => {
    if (!deviceId || pinging) return;
    setPinging(true);
    try {
      const result = await programmingService.ping(deviceId);
      setOnline(result.online);
      setLatencyMs(result.latency_ms);
      setIdentification(result.identification);
      addLog('out', result.sent);
      if (result.reply) addLog('in', result.reply);
      else addLog('info', 'ping: no reply');
    } catch (err) {
      setOnline(null);
      addLog('error', `ping: ${errorDetail(err)}`);
    } finally {
      setPinging(false);
    }
  }, [deviceId, pinging, addLog]);

  // First activation: catalogue, then ping.
  useEffect(() => {
    if (!active || !deviceId || loadedFor.current === deviceId) return;
    loadedFor.current = deviceId;
    let cancelled = false;
    programmingService.getCatalogue(deviceId)
      .then((cat) => {
        if (cancelled) return;
        setCatalogue(cat);
        setCatalogueFailure(null);
        addLog('info', `catalogue ${cat.type}: ${cat.commands.length} commands`);
        return ping();
      })
      .catch((err) => {
        if (cancelled) return;
        const status = errorStatus(err);
        setCatalogueFailure(status === 404 ? 'none' : status === 403 ? 'forbidden' : 'failed');
        addLog('error', `catalogue: ${errorDetail(err)}`);
      });
    return () => {
      cancelled = true;
    };
    // ping is stable enough for a first load; re-running on it would double the ping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, deviceId]);

  const commandsByGroup = useMemo(() => {
    const map: Record<string, ProgrammingCommand[]> = {};
    for (const command of catalogue?.commands ?? []) {
      (map[command.group] ??= []).push(command);
    }
    return map;
  }, [catalogue]);

  const readableCodes = useCallback((commands: ProgrammingCommand[]) =>
    commands.filter((c) => c.ops.includes('read') && !c.indexed).map((c) => c.code), []);

  const readCodes = useCallback(async (codes: string[], index?: number) => {
    if (!deviceId || codes.length === 0) return [] as ReadItem[];
    const items = await programmingService.read(deviceId, codes, index);
    setValues((prev) => {
      const next = { ...prev };
      for (const item of items) next[item.code] = item;
      return next;
    });
    for (const item of items) {
      if (item.sent) addLog('out', item.sent);
      if (item.status === 'ok' && item.reply) addLog('in', item.reply);
      else if (item.status === 'timeout') addLog('info', `${item.cmd}: no reply`);
    }
    if (items.some((i) => i.status === 'offline')) {
      setOnline(false);
      addLog('info', 'gateway considered offline; batch stopped');
    } else if (items.some((i) => i.status === 'ok')) {
      setOnline(true);
      setLastReadAt(new Date());
    }
    return items;
  }, [deviceId, addLog]);

  const readGroup = useCallback(async (groupCode: string) => {
    if (readingGroup) return;
    const codes = readableCodes(commandsByGroup[groupCode] ?? []);
    setReadingGroup(groupCode);
    try {
      await readCodes(codes);
    } catch (err) {
      addLog('error', `read ${groupCode}: ${errorDetail(err)}`);
    } finally {
      setReadingGroup(null);
    }
  }, [readingGroup, readableCodes, commandsByGroup, readCodes, addLog]);

  const readAll = useCallback(async () => {
    if (readingGroup || !catalogue) return;
    const groups = catalogue.groups.map((g) => g.code).filter((g) => readableCodes(commandsByGroup[g] ?? []).length > 0);
    setReadProgress({ done: 0, total: groups.length });
    try {
      for (let i = 0; i < groups.length; i += 1) {
        setReadingGroup(groups[i]);
        const items = await readCodes(readableCodes(commandsByGroup[groups[i]] ?? []));
        setReadProgress({ done: i + 1, total: groups.length });
        if (items.some((it) => it.status === 'offline')) break;
      }
    } catch (err) {
      addLog('error', `read all: ${errorDetail(err)}`);
    } finally {
      setReadingGroup(null);
      setReadProgress(null);
    }
  }, [readingGroup, catalogue, commandsByGroup, readableCodes, readCodes, addLog]);

  /** One command, with its index when the catalogue asks for one. */
  const readOne = useCallback(async (code: string, index?: number) => {
    if (readingGroup) return;
    setReadingGroup(code);
    try {
      await readCodes([code], index);
    } catch (err) {
      addLog('error', `read ${code}${index !== undefined ? `[${index}]` : ''}: ${errorDetail(err)}`);
    } finally {
      setReadingGroup(null);
    }
  }, [readingGroup, readCodes, addLog]);
  const readIndexed = readOne;

  const stage = useCallback((code: string, change: StagedChange | null) => {
    setStaged((prev) => {
      const next = { ...prev };
      if (change === null) delete next[code];
      else next[code] = change;
      return next;
    });
  }, []);

  const setIndex = useCallback((code: string, index: number) => {
    setIndexes((prev) => ({ ...prev, [code]: index }));
  }, []);

  const stagedChanges = useMemo((): ProgrammingChange[] =>
    Object.entries(staged).map(([code, change]) => ({ code, op: change.op, value: change.value, index: change.index })),
    [staged]);

  const applyResults = useCallback((items: WriteItem[]) => {
    setLastResults(items);
    setValues((prev) => {
      const next = { ...prev };
      for (const item of items) {
        if (item.op === 'action') continue;
        if (item.status === 'applied' || item.status === 'mismatch') {
          next[item.code] = {
            code: item.code, cmd: item.cmd, op: 'read', status: 'ok',
            raw: item.raw ?? null, value: item.read_back, latency_ms: item.latency_ms ?? null,
          };
        }
      }
      return next;
    });
    for (const item of items) {
      addLog('out', item.sent);
      if (item.reply) addLog('in', item.reply);
      addLog(item.status === 'mismatch' ? 'error' : 'info', `${item.cmd}: ${item.status}`);
    }
  }, [addLog]);

  const send = useCallback(async (changes: ProgrammingChange[]): Promise<WriteItem[]> => {
    if (!deviceId || changes.length === 0 || writing) return [];
    setWriting(true);
    try {
      const items = await programmingService.write(deviceId, changes);
      applyResults(items);
      setStaged((prev) => {
        const next = { ...prev };
        for (const item of items) if (item.status === 'applied' || item.status === 'sent') delete next[item.code];
        return next;
      });
      return items;
    } catch (err) {
      addLog('error', `write: ${errorDetail(err)}`);
      throw err;
    } finally {
      setWriting(false);
    }
  }, [deviceId, writing, applyResults, addLog]);

  const submitStaged = useCallback(() => send(stagedChanges), [send, stagedChanges]);

  const sendRaw = useCallback(async (payload: string) => {
    if (!deviceId || rawBusy || !payload.trim()) return;
    setRawBusy(true);
    try {
      const result = await programmingService.raw(deviceId, payload.trim());
      addLog('out', result.sent);
      if (result.reply) addLog('in', result.reply);
      else addLog('info', `${result.sent}: no reply`);
      setOnline(result.status === 'ok' ? true : online);
    } catch (err) {
      addLog('error', `raw: ${errorDetail(err)}`);
    } finally {
      setRawBusy(false);
    }
  }, [deviceId, rawBusy, online, addLog]);

  const busy = pinging || readingGroup !== null || writing || rawBusy;

  return {
    catalogue, catalogueFailure, commandsByGroup,
    online, latencyMs, identification, pinging, ping,
    mode, setMode,
    values, readingGroup, readProgress, readGroup, readAll, readOne, readIndexed,
    staged, stage, stagedChanges, indexes, setIndex,
    writing, lastResults, lastReadAt, send, submitStaged,
    sendRaw, rawBusy,
    log, clearLog, busy,
  };
};
